import { Component, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { AuthService } from "../../../services/auth.service";
import { BusyIconService } from "../../../services/busy.service";
import { UserService } from "../../../services/user.service";
import { MailService } from "../../../services/mail.service";
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from "angular-l10n";
import { environment } from "../../../../environments/environment";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { EToolMode } from "../../../models/toolMode.enum";


@Component({
  selector: "login",
  templateUrl: "login.template.html",
  styleUrls: ["../login.style.less"],
  encapsulation: ViewEncapsulation.None
})

export class LoginComponent implements OnInit {
  user = {username: "", password: ""};
  resetPasswordUsername: string = "";
  showPasswordDialog: boolean = false;
  error: string = "";
  infoMessages: any[] = [];

  // TOTP
  showTotpDialog: boolean = false;
  totpUserId: string = "";
  totpCode: string = "";

  // app selection
  showAppSelection: boolean = false;
  uTenderApps: { icon: IconProp, name: string, key: EToolMode, active: boolean }[] = [
    {icon: "coins", name: "Tender", key: EToolMode.SOURCE, active: true},
    {icon: "clipboard-list", name: "RFI", key: EToolMode.RFI, active: true},
    {icon: "file-contract", name: "Contract", key: EToolMode.CONTRACT, active: true},
    {icon: "balance-scale", name: "Auction", key: EToolMode.AUCTION, active: true},
    {icon: "chart-pie", name: "KPI", key: EToolMode.KPI, active: true},
    {icon: "file-invoice-dollar", name: "Book and Control", key: EToolMode.BOOK_AND_CONTROL, active: true},
    {icon: "globe-americas", name: "Network Optimizer", key: EToolMode.NETWORK_OPTIMIZER, active: true},
    {icon: "coins", name: "Toll Tool", key: EToolMode.TOLL_TOOL, active: true}
  ];

  constructor(@Inject(L10N_LOCALE) public locale: L10nLocale, private translation: L10nTranslationService, private mailService: MailService, private router: Router, private userService: UserService, private authService: AuthService, private busyIconService: BusyIconService, private route: ActivatedRoute) {
  }

  logIn() {
    this.error = "";
    const loginSubscription: Subscription = this.authService.login(this.user.username, this.user.password).subscribe((result) => {
        if (result) {
          if (result.requestTotpValue) {
            this.totpUserId = result.userId;
            this.totpCode = "";
            this.showTotpDialog = true;
          } else {
            const modes = this.authService.getModes();
            if (modes) {
              if ((modes.indexOf(EToolMode.PREMIUM) !== -1 && modes.length === 1) || modes.indexOf(EToolMode.SUPPLIER) !== -1) {
                const mode = modes.indexOf(EToolMode.PREMIUM) !== -1 ? EToolMode.PREMIUM : EToolMode.SUPPLIER;
                const requestTokenSubscriptions = this.authService.requestLoginToken(mode).subscribe((tokenResponse) => {
                  this.handleRequestToken(tokenResponse, false);
                });
                this.busyIconService.showBusyIcon(requestTokenSubscriptions);
              } else if (modes.indexOf(EToolMode.NONE) !== -1 || modes.length === 0) {
                this.executeLogout();
              } else {
                this.updateAvailableApps(modes);
                this.showAppSelection = true;
              }
            }
          }
        } else {
          this.router.navigate(["/login"], {queryParams: {unhandled: "true"}, queryParamsHandling: "preserve"});
        }
      },
      (err) => {
        this.user.password = "";
        this.error = err;
      });
    this.busyIconService.showBusyIcon(loginSubscription);
  }

  updateAvailableApps(appsArray: EToolMode[]): void {
    const uTenderApps = [];
    if (appsArray.indexOf(EToolMode.PREMIUM) !== -1) {
      uTenderApps.push({
        icon: "star",
        name: "Premium",
        key: EToolMode.PREMIUM,
        active: true
      });
      if (appsArray.indexOf(EToolMode.SOURCE) !== -1) {
        uTenderApps.push({
          icon: "coins",
          name: "Tender",
          key: EToolMode.SOURCE,
          active: appsArray.indexOf(EToolMode.SOURCE) !== -1
        });
      }
      if (appsArray.indexOf(EToolMode.RFI) !== -1) {
        uTenderApps.push({
          icon: "clipboard-list",
          name: "RFI",
          key: EToolMode.RFI,
          active: appsArray.indexOf(EToolMode.RFI) !== -1
        });
      }
      if (appsArray.indexOf(EToolMode.CONTRACT) !== -1) {
        uTenderApps.push({
          icon: "file-contract",
          name: "Contract",
          key: EToolMode.CONTRACT,
          active: appsArray.indexOf(EToolMode.CONTRACT) !== -1
        });
      }
      if (appsArray.indexOf(EToolMode.AUCTION) !== -1) {
        uTenderApps.push({
          icon: "balance-scale",
          name: "Auction",
          key: EToolMode.AUCTION,
          active: appsArray.indexOf(EToolMode.AUCTION) !== -1
        });
      }
      if (appsArray.indexOf(EToolMode.KPI) !== -1) {
        uTenderApps.push({
          icon: "chart-pie",
          name: "KPI",
          key: EToolMode.KPI,
          active: appsArray.indexOf(EToolMode.KPI) !== -1
        });
      }
      if (appsArray.indexOf(EToolMode.BOOK_AND_CONTROL) !== -1) {
        uTenderApps.push({
          icon: "file-invoice-dollar",
          name: "Book and Control",
          key: EToolMode.BOOK_AND_CONTROL,
          active: appsArray.indexOf(EToolMode.BOOK_AND_CONTROL) !== -1
        });
      }
      if (appsArray.indexOf(EToolMode.NETWORK_OPTIMIZER) !== -1) {
        uTenderApps.push({
          icon: "globe-americas",
          name: "Network Optimizer",
          key: EToolMode.NETWORK_OPTIMIZER,
          active: appsArray.indexOf(EToolMode.NETWORK_OPTIMIZER) !== -1
        });
      }
      if (appsArray.indexOf(EToolMode.TOLL_TOOL) !== -1) {
        uTenderApps.push({
          icon: "coins",
          name: "Toll Calculator",
          key: EToolMode.TOLL_TOOL,
          active: appsArray.indexOf(EToolMode.TOLL_TOOL) !== -1
        });
      }
    } else {
      Array.prototype.push.apply(uTenderApps, [
        {icon: "coins", name: "Tender", key: EToolMode.SOURCE, active: appsArray.indexOf(EToolMode.SOURCE) !== -1},
        {icon: "clipboard-list", name: "RFI", key: EToolMode.RFI, active: appsArray.indexOf(EToolMode.RFI) !== -1},
        {
          icon: "file-contract",
          name: "Contract",
          key: EToolMode.CONTRACT,
          active: appsArray.indexOf(EToolMode.CONTRACT) !== -1
        },
        {
          icon: "balance-scale",
          name: "Auction",
          key: EToolMode.AUCTION,
          active: appsArray.indexOf(EToolMode.AUCTION) !== -1
        },
        {icon: "chart-pie", name: "KPI", key: EToolMode.KPI, active: appsArray.indexOf(EToolMode.KPI) !== -1},
        {
          icon: "file-invoice-dollar",
          name: "Book and Control",
          key: EToolMode.BOOK_AND_CONTROL,
          active: appsArray.indexOf(EToolMode.BOOK_AND_CONTROL) !== -1
        },
        {
          icon: "globe-americas",
          name: "Network Optimizer",
          key: EToolMode.NETWORK_OPTIMIZER,
          active: appsArray.indexOf(EToolMode.NETWORK_OPTIMIZER) !== -1
        },
        {
          icon: "coins",
          name: "Toll Calculator",
          key: EToolMode.TOLL_TOOL,
          active: appsArray.indexOf(EToolMode.TOLL_TOOL) !== -1
        }
      ]);
    }
    this.uTenderApps = uTenderApps;
  }

  handleRequestToken(tokenResponse, admin: boolean) {
    if (tokenResponse.requestToken) {
      if (admin) {
        window.location.href = `${environment.clientUrl}/login/?login=${tokenResponse.requestToken}&admin=true`;
      } else {
        window.location.href = `${environment.clientUrl}/login/?login=${tokenResponse.requestToken}`;
      }
    }
  }

  handleAppRequestToken(tokenResponse, app: EToolMode): void {
    if (tokenResponse.requestToken) {
      switch (app) {
        case EToolMode.SOURCE:
          window.location.href = `${environment.sourceUrl}/login/?login=${tokenResponse.requestToken}`;
          break;
        case EToolMode.RFI:
          window.location.href = `${environment.rfiUrl}/login/?login=${tokenResponse.requestToken}`;
          break;
        case EToolMode.CONTRACT:
          window.location.href = `${environment.contractUrl}/login/?login=${tokenResponse.requestToken}`;
          break;
        case EToolMode.KPI:
          window.location.href = `${environment.reporterUrl}/login/?login=${tokenResponse.requestToken}`;
          break;
        case EToolMode.NETWORK_OPTIMIZER:
          window.location.href = `${environment.networkOptimizerUrl}/login/?login=${tokenResponse.requestToken}`;
          break;
        case EToolMode.TOLL_TOOL:
          window.location.href = `${environment.tollToolUrl}/login/?login=${tokenResponse.requestToken}`;
          break;
      }
    }
  }

  submitTotpCode() {
    const totpSubscription = this.authService.submitTotp(this.totpUserId, this.totpCode).subscribe((result) => {
      if (result) {
        window.location.href = environment.clientUrl;
        this.handleRequestToken(result, false);
      }
    }, (error) => {
      this.error = error;
      this.user.password = "";
      this.showTotpDialog = false;
    });
    this.busyIconService.showBusyIcon(totpSubscription);
  }

  openRenewalDialog() {
    this.resetPasswordUsername = this.user.username;
    this.showPasswordDialog = true;
  }

  sendPasswordReset() {
    this.authService.requestPasswordReset(this.resetPasswordUsername).subscribe();
    this.showPasswordDialog = false;
  }

  executeLogout(): void {
    this.authService.logout();
    this.infoMessages.push({
      severity: "success",
      summary: "Logout: ",
      detail: "You logged out successfully"
    });
    window.location.replace(environment.loginUrl);
  }

  ngOnInit() {
    if (this.route.snapshot.queryParamMap.has("adminToken")) {
      this.handleAdminToken();
    } else if (this.route.snapshot.queryParamMap.get("mailValidated")) {
      this.infoMessages.push({
        severity: "success",
        summary: "Mail Validated: ",
        detail: "Thank you for validating your mail address."
      });
    } else if (this.route.snapshot.queryParamMap.get("logout")) {
      this.executeLogout();
    } else if (this.route.snapshot.queryParamMap.get("error")) {
      const error = parseInt(this.route.snapshot.queryParamMap.get("error"), 10);
      this.authService.logout();
      if (error === 1) {
        this.infoMessages.push({
          severity: "error",
          summary: "Login failed: ",
          detail: "Please try again"
        });
      } else {
        this.infoMessages.push({
          severity: "error",
          summary: "Not logged in: ",
          detail: "Please login to our system"
        });
      }
    } else {
      this.authService.$loggedIn.subscribe((success: boolean) => {
        if (success) {
          const modes = this.authService.getModes();
          if (modes.indexOf(EToolMode.SUPPLIER) !== -1 || (modes.indexOf(EToolMode.PREMIUM) !== -1 && modes.length === 1)) {
            const mode = modes.indexOf(EToolMode.PREMIUM) !== -1 ? EToolMode.PREMIUM : EToolMode.SUPPLIER;
            const busy = this.authService.requestLoginToken(mode).subscribe((result) => {
              this.handleRequestToken(result, false);
            });
            this.busyIconService.showBusyIcon(busy);
          } else if (modes.indexOf(EToolMode.NONE) !== -1 || modes.length === 0) {
            this.executeLogout();
          } else {
            this.updateAvailableApps(modes);
            this.showAppSelection = true;
          }
        }
      });
      this.infoMessages = [];
    }
  }

  handleAdminToken() {
    const adminToken = this.route.snapshot.queryParamMap.get("adminToken");
    const loginSubscription: Subscription = this.authService.tokenLogin(adminToken).subscribe((result) => {
        this.handleTokenLogin(result, true);
      },
      (err) => {
        this.error = err;
      });
    this.busyIconService.showBusyIcon(loginSubscription);
  }

  handleTokenLogin(result, admin: boolean = false) {
    const modes = this.authService.getModes();
    if (modes.indexOf(EToolMode.PREMIUM) !== -1 || modes.indexOf(EToolMode.SUPPLIER) !== -1) {
      const mode = modes.indexOf(EToolMode.PREMIUM) !== -1 ? EToolMode.PREMIUM : EToolMode.SUPPLIER;
      if (result) {
        console.log(mode);
        const requestTokenSubscriptions = this.authService.requestLoginToken(mode).subscribe((tokenResponse) => {
          this.handleRequestToken(tokenResponse, admin);
        });
        this.busyIconService.showBusyIcon(requestTokenSubscriptions);
      } else {
        this.router.navigate(["/login"], {queryParams: {unhandled: "true"}, queryParamsHandling: "preserve"});
      }
    }
  }

  navigateToSSOLogin() {
    this.router.navigate(["/externalLogin"], {queryParams: {unhandled: "true"}, queryParamsHandling: "preserve"});
  }

  selectAndLogin(key: EToolMode) {
    const requestTokenSubscriptions = this.authService.requestLoginToken(key).subscribe((tokenResponse) => {
      if (key === EToolMode.PREMIUM) {
        this.handleRequestToken(tokenResponse, false);
      } else {
        this.handleAppRequestToken(tokenResponse, key);
      }
    });
    this.busyIconService.showBusyIcon(requestTokenSubscriptions);
  }
}
